import type { CalendarItem, CalendarRange } from './calendar-item';

// Work the owner's agents do, as the Calendar draws it: an agent's task (it names the agent) or a routine run. A routine
// has no owning agent (the agent a routine read names is only the one that last ran it), so its runs share one
// "Routines" lane instead of a face. Nothing here is sample data: a check means the work really happened at that time.

/** The Day view's lane, the filter row and the Agents switch all key agent work by this. */
export const ROUTINES_KEY = 'routines';

export function isAgentItem(item: Pick<CalendarItem, 'kind' | 'ref'>): boolean {
  return (item.kind === 'task' && (!!item.ref.agentId || !!item.ref.routineId)) || item.kind === 'routine';
}

/** `agent:<id>` for an agent's task, `routines` for a routine run or a routine's work, nothing for the owner's own items. */
export function agentKeyOf(item: Pick<CalendarItem, 'kind' | 'ref'>): string | undefined {
  if (item.kind === 'routine' || (item.kind === 'task' && item.ref.routineId)) return ROUTINES_KEY;
  return item.kind === 'task' && item.ref.agentId ? `agent:${item.ref.agentId}` : undefined;
}

export interface AgentLane {
  key: string;
  /** Absent for the Routines lane. */
  agentId?: string;
  name?: string;
}

/** Every agent with work among `items`, by name (an unnamed one by id), then Routines when a routine runs. */
export function agentLanes(items: CalendarItem[]): AgentLane[] {
  const agents = new Map<string, AgentLane>();
  let routines = false;
  for (const item of items) {
    const key = agentKeyOf(item);
    if (!key) continue;
    if (key === ROUTINES_KEY) routines = true;
    else if (!agents.has(key) || (!agents.get(key)?.name && item.agentName)) {
      agents.set(key, { key, agentId: item.ref.agentId, ...(item.agentName ? { name: item.agentName } : {}) });
    }
  }
  const sorted = [...agents.values()].sort(
    (a, b) => (a.name ?? '￿').localeCompare(b.name ?? '￿') || (a.agentId ?? '').localeCompare(b.agentId ?? ''),
  );
  return routines ? [...sorted, { key: ROUTINES_KEY }] : sorted;
}

const startsIn = (item: Pick<CalendarItem, 'start'>, range: CalendarRange) => item.start >= range.from && item.start < range.to;

/** "{n} handled by your agents": the agent work in the shown days that really happened. */
export function handledCount(items: CalendarItem[], range: CalendarRange): number {
  return new Set(items.filter((item) => isAgentItem(item) && item.done && startsIn(item, range)).map((item) => item.key)).size;
}

/** The fields of a listed task the live line reads. `WebTask` satisfies it. */
export interface LiveTask {
  id: string;
  title?: string;
  agentId?: string;
  /** The routine whose run opened the task: its work is the routine's, never the agent's. */
  pulseIdentityId?: string;
  runInFlight?: boolean;
  lastActivityAt?: string;
}

/**
 * The work running now, in the lane it belongs to: an agent's own task (`agent:<id>`, the agent's dot and name), or a
 * routine's work (`routines`, the routine mark and the routine's name), whichever agent runs it.
 */
export interface LiveAgentWork {
  taskId: string;
  title: string;
  laneKey: string;
  agentId?: string;
  agentName?: string;
  routineId?: string;
  routineName?: string;
}

/**
 * The work running right now (`runInFlight` on the task list) that can be named and shown: the most recently active task
 * whose agent, or whose routine for a routine's work, has a name and whose lane is not hidden. A newer task that cannot be
 * named (its routine deleted or not read yet), or whose lane the owner hid, never hides one that can; none when nothing
 * nameable runs in a shown lane.
 */
export function liveAgentWork(
  tasks: LiveTask[],
  agentNameOf: (agentId: string) => string | undefined = () => undefined,
  routineNameOf: (routineId: string) => string | undefined = () => undefined,
  hiddenLanes: ReadonlySet<string> = new Set(),
): LiveAgentWork | undefined {
  const running = tasks
    .filter((task) => task?.id && (task.pulseIdentityId || task.agentId) && task.runInFlight === true && task.title)
    .sort((a, b) => (Date.parse(b.lastActivityAt ?? '') || 0) - (Date.parse(a.lastActivityAt ?? '') || 0) || a.id.localeCompare(b.id));
  for (const task of running) {
    const title = task.title ?? '';
    if (task.pulseIdentityId) {
      if (hiddenLanes.has(ROUTINES_KEY)) continue;
      const routineName = routineNameOf(task.pulseIdentityId);
      if (routineName) return { taskId: task.id, title, laneKey: ROUTINES_KEY, routineId: task.pulseIdentityId, routineName };
      continue;
    }
    if (task.agentId && hiddenLanes.has(`agent:${task.agentId}`)) continue;
    const agentName = task.agentId ? agentNameOf(task.agentId) : undefined;
    if (agentName && task.agentId) return { taskId: task.id, title, laneKey: `agent:${task.agentId}`, agentId: task.agentId, agentName };
  }
  return undefined;
}

/** The owner's own Day column, which keeps whatever has no column of its own. */
export const MINE_COLUMN_KEY = 'mine';

/**
 * Which column the live line is drawn in: the running agent's own Day column, else the owner's when that agent has no
 * column that day; on the week grid, today's column. -1 when none is on screen.
 */
export function liveLineColumn(columns: { key: string; day: number }[], laneKey: string, today: number, dayColumns: boolean): number {
  if (!dayColumns) return columns.findIndex((column) => column.day === today);
  const own = columns.findIndex((column) => column.key === laneKey);
  return own >= 0 ? own : columns.findIndex((column) => column.key === MINE_COLUMN_KEY);
}
